// The shell top-bar breadcrumb derivation (UI-LAW §2.11: the breadcrumb is
// the default page identity). Pure route-state → crumb mapping; parent-crumb
// clicks project their route's return search (the working scope rides along).

import type { useNavigate } from "@tanstack/react-router";

import { transcriptListReturnSearch } from "./routerSearch";
import { shortResourceId, type ShellRouteState } from "./shellNavigation";

export interface ShellBreadcrumbItem {
  label: string;
  onClick?: () => void;
}

export interface BreadcrumbInput {
  /** Display name of the board on /dashboard/$dashboardId, once loaded. */
  dashboardName?: string;
  navigate: ReturnType<typeof useNavigate>;
  routeState: ShellRouteState;
  /** Display name of the Scenario owning /scenarios/$scenarioId/rows. */
  scenarioName?: string;
}

function simulationActionLabel(
  action: NonNullable<ShellRouteState["simulationAction"]>,
): string {
  if (action === "templateCreate") {
    return "New template";
  }
  if (action === "templateEdit") {
    return "Edit template";
  }
  return "Create simulation";
}

export function buildBreadcrumbs({
  dashboardName,
  navigate,
  routeState,
  scenarioName,
}: BreadcrumbInput): ShellBreadcrumbItem[] {
  if (routeState.area === "transcripts") {
    if (routeState.transcriptId) {
      return [
        {
          label: "Transcripts",
          onClick: () =>
            void navigate({
              search: transcriptListReturnSearch,
              to: "/transcripts",
            }),
        },
        { label: `Transcript ${shortResourceId(routeState.transcriptId)}` },
      ];
    }
    return [{ label: "Transcripts" }];
  }

  if (routeState.area === "markers") {
    if (routeState.markerAction !== undefined) {
      return [
        {
          label: "Markers",
          onClick: () =>
            void navigate({ search: (prev) => prev, to: "/markers" }),
        },
        { label: routeState.markerAction === "create" ? "Create" : "Edit" },
      ];
    }
    return [{ label: "Markers" }];
  }

  if (routeState.area === "simulations") {
    if (routeState.simulationAction !== undefined) {
      return [
        {
          label: "Simulations",
          // Only the working scope rides back to the queue: an authoring
          // route's own params (`?from=`, a template id) never leak onto the
          // hub.
          onClick: () =>
            void navigate({
              search: ({ agent, team }) => ({ agent, team }),
              to: "/simulations",
            }),
        },
        { label: simulationActionLabel(routeState.simulationAction) },
      ];
    }
    return [{ label: "Simulations" }];
  }

  if (routeState.area === "agent") {
    if (
      routeState.authoring === "variables" &&
      routeState.agentId !== undefined
    ) {
      const agentId = routeState.agentId;
      return [
        {
          label: "Agent",
          onClick: () =>
            void navigate({
              params: { agentId },
              search: {},
              to: "/agents/$agentId",
            }),
        },
        { label: "Variables" },
      ];
    }
    return [{ label: "Agent" }];
  }

  if (routeState.area === "agents") {
    return [{ label: "Agents" }];
  }

  if (routeState.area === "personas") {
    return [{ label: "Personas" }];
  }

  // Scenario sets are a `/scenarios` tab, not a route: the crumb names the
  // surface, like Alerts (docs/09 §4).
  if (routeState.area === "scenarios") {
    if (routeState.scenarioId) {
      return [
        {
          label: "Scenarios",
          onClick: () =>
            void navigate({
              search: ({ agent, team }) => ({ agent, team }),
              to: "/scenarios",
            }),
        },
        {
          label:
            scenarioName ??
            `Scenario ${shortResourceId(routeState.scenarioId)}`,
        },
        { label: "Rows" },
      ];
    }
    return [{ label: "Scenarios" }];
  }

  if (routeState.area === "dashboard") {
    if (routeState.dashboardId) {
      return [
        {
          label: "Dashboard",
          onClick: () =>
            void navigate({ search: (prev) => prev, to: "/dashboard" }),
        },
        {
          label:
            dashboardName ?? `Board ${shortResourceId(routeState.dashboardId)}`,
        },
      ];
    }
    return [{ label: "Dashboard" }];
  }

  if (routeState.area === "review") {
    return [{ label: "Review" }];
  }

  if (routeState.area === "alerts") {
    if (routeState.alertAction !== undefined) {
      return [
        {
          label: "Alerts",
          onClick: () => void navigate({ search: true, to: "/alerts" }),
        },
        {
          label:
            routeState.alertAction === "create"
              ? "Create rule"
              : "Silence rule",
        },
      ];
    }
    return [{ label: "Alerts" }];
  }

  // The only remaining area is settings.
  if (routeState.settingsTab === "teams") {
    const items: ShellBreadcrumbItem[] = [
      { label: "Settings" },
      {
        label: "Teams",
        onClick: () => void navigate({ to: "/settings/teams" }),
      },
    ];
    if (routeState.teamId) {
      items.push({ label: `Team ${shortResourceId(routeState.teamId)}` });
    }
    return items;
  }

  // The bare `/settings` index redirects before render (settingsIndexTarget);
  // if it ever paints, the crumb is just "Settings".
  if (routeState.settingsTab === undefined) {
    return [{ label: "Settings" }];
  }

  const settingsLabels: Record<
    NonNullable<typeof routeState.settingsTab>,
    string
  > = {
    agent: "Agent",
    agents: "Agents",
    "api-keys": "API keys",
    "audit-log": "Audit log",
    billing: "Billing",
    invitations: "Invitations",
    license: "License",
    members: "Members & roles",
    organization: "Organization",
    "phone-numbers": "Phone numbers",
    profile: "Profile",
    retention: "Retention",
    usage: "Usage",
  };
  return [
    { label: "Settings" },
    { label: settingsLabels[routeState.settingsTab] },
  ];
}
